import * as React from 'react';
import { useCallback, useMemo } from 'react';

export interface SelectedItemStateProps {
  chartUuid: string;
  chartType?: string;
  title?: string;
  description?: string;
  size?: string;
  uuid?: string;
}

interface DashboardEditSessionContextProps {
  onToggleDashboardModal: () => void;
  isDashboardModalOpen: boolean;
  dashboardState: Array<SelectedItemStateProps>;
  updateDashboardState: (newState: SelectedItemStateProps[]) => void;
  removeCardFromDashboard: (chartUuid: string | null | undefined, uuid?: string) => void;
}

interface DashboardEditSessionProviderProps {
  children: React.ReactNode;
}

const DashboardEditSessionContext = React.createContext<DashboardEditSessionContextProps | undefined>(undefined);

export const DashboardEditSessionProvider: React.FC<DashboardEditSessionProviderProps> = ({ children }) => {
  const [dashboardState, setDashboardState] = React.useState<SelectedItemStateProps[]>([]);
  const [isDashboardModalOpen, setIsDashboardModalOpen] = React.useState(false);

  const updateDashboardState = useCallback((newState: SelectedItemStateProps[]) => {
    setDashboardState(newState);
  }, []);

  const onToggleDashboardModal = useCallback(() => {
    setIsDashboardModalOpen(prevState => !prevState);
  }, []);

  const removeCardFromDashboard = useCallback((chartUuid: string | null | undefined, uuid?: string) => {
    if (!uuid && !chartUuid) {
      return;
    }

    setDashboardState(prevState => prevState.filter(x => (uuid ? x.uuid !== uuid : x.chartUuid !== chartUuid)));
  }, []);

  const value = useMemo(
    () => ({
      onToggleDashboardModal,
      isDashboardModalOpen,
      dashboardState,
      updateDashboardState,
      removeCardFromDashboard,
    }),
    [dashboardState, isDashboardModalOpen, onToggleDashboardModal, removeCardFromDashboard, updateDashboardState],
  );

  return <DashboardEditSessionContext.Provider value={value}>{children}</DashboardEditSessionContext.Provider>;
};

export const useDashboardEditSession = (): DashboardEditSessionContextProps => {
  const context = React.useContext(DashboardEditSessionContext);

  if (!context) {
    throw new Error('useDashboardEditSession must be used within DashboardEditSessionProvider');
  }

  return context;
};
